// Package Imports
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureFlag } from '../../enums/feature-flag/feature-flag.enum';

// Models
import { AuthUser, UserSessionData } from '../../models';
import { FeatureFlag as FeatureFlagModel} from '../../models/feature-flag/feature-flag';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

// Services
import { SessionService } from '../../services/session/session.service';
import { UserService } from '../../services/user/user.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit {
  userData: UserSessionData;

  constructor(private readonly router: Router,
              private readonly sessionService: SessionService,
              private readonly userService: UserService,
              private readonly featureFlagService: FeatureFlagService) { }

  ngOnInit(): void {
    this.userData = this.userSession;

    if (!this.userData.user) {
      this.userData.user = new AuthUser();
    }

    this.userService.getUserInfo().then((user) => {
      this.userData.user = user.data;

      if (this.userData == null || this.userData.user == null) {
        this.router.navigate(['/unauthorized/401']);

        return;
      }

      if (this.userData.user.type === 'MEMBER' ||
          this.userData.user.rights.openEnrollmentAddEditDelete ||
          this.userData.user.rights.openEnrollmentViewOnly) {
        this.userData.user.isAuthorized = true;

        if (this.userData.user.isLogged == null) {
          this.userData.user.isLogged = true;
        }
      }
      else {
        this.userData.user.isAuthorized = false;
        this.sessionService.storeSessionData(this.sessionService.sessionKey_UserSession, this.userData);
        this.router.navigate(['/unauthorized/401']);

        return;
      }

      this.sessionService.storeSessionData(this.sessionService.sessionKey_UserSession, this.userData);

      if (this.userData.user.type === 'MEMBER') {
        this.router.navigate(['/member/home']);
      }
      else {
        this.router.navigate(['/admin/home']);
      }

      this.featureFlagService
        .getFeatureFlagApi(FeatureFlag.AccessToRollingOpenEnrollments)
        .then((accessToRollingOpenEnrollments) => {
          const accessToRollingOpenEnrollmentsValue = new FeatureFlagModel();
          accessToRollingOpenEnrollmentsValue.name = FeatureFlag.AccessToRollingOpenEnrollments;
          accessToRollingOpenEnrollmentsValue.enabled = accessToRollingOpenEnrollments.data;

          this.userData.featureFlags.push(accessToRollingOpenEnrollmentsValue);
          const userData = this.userData;
          this.sessionService.storeSessionData(
            this.sessionService.sessionKey_UserSession,
            userData
          );
                    
          this.sessionService.pushFeature(
            userData.featureFlags
          );
        });

      this.featureFlagService
        .getFeatureFlagApi(FeatureFlag.OEAttachmentEnhancement)
        .then((oeAttachmentEnhancement) => {
          const oeAttachmentEnhancementValue = new FeatureFlagModel();
          oeAttachmentEnhancementValue.name = FeatureFlag.OEAttachmentEnhancement;
          oeAttachmentEnhancementValue.enabled = oeAttachmentEnhancement.data;

          this.userData.featureFlags.push(oeAttachmentEnhancementValue);
          const userData = this.userData;
          this.sessionService.storeSessionData(
            this.sessionService.sessionKey_UserSession,
            userData
          );
                    
          this.sessionService.pushFeature(
            userData.featureFlags
          );
        });

      this.featureFlagService
        .getFeatureFlagApi(FeatureFlag.OEPlanDefaultOptions)
        .then((oePlanDefaultOptions) => {
          const oePlanDefaultOptionsValue = new FeatureFlagModel();
          oePlanDefaultOptionsValue.name = FeatureFlag.OEPlanDefaultOptions;
          oePlanDefaultOptionsValue.enabled = oePlanDefaultOptions.data;

          this.userData.featureFlags.push(oePlanDefaultOptionsValue);
          const userData = this.userData;
          this.sessionService.storeSessionData(
            this.sessionService.sessionKey_UserSession,
            userData
          );
                    
          this.sessionService.pushFeature(
            userData.featureFlags
          );
        });

      this.featureFlagService
        .getFeatureFlagApi(FeatureFlag.EnablePlanCategoryNewImplementation)
        .then((accessToRollingOpenEnrollments) => {
          const enablePlanCategoryNewImplementationValue = new FeatureFlagModel();
          enablePlanCategoryNewImplementationValue.name = FeatureFlag.EnablePlanCategoryNewImplementation;
          enablePlanCategoryNewImplementationValue.enabled = accessToRollingOpenEnrollments?.data ? accessToRollingOpenEnrollments?.data : false;

          this.userData.featureFlags.push(enablePlanCategoryNewImplementationValue);
          const userData = this.userData;
          this.sessionService.storeSessionData(
            this.sessionService.sessionKey_UserSession,
            userData
          );

          this.sessionService.pushFeature(
            userData.featureFlags
          );
          });

        this.featureFlagService
        .getFeatureFlagApi(FeatureFlag.EnableRetroOENotifications)
        .then((enableRetroOENotifications) => {
          const enableRetroOENotificationsValue = new FeatureFlagModel();
          enableRetroOENotificationsValue.name = FeatureFlag.EnableRetroOENotifications;
          enableRetroOENotificationsValue.enabled = enableRetroOENotifications.data;

          this.userData.featureFlags.push(enableRetroOENotificationsValue);
          const userData = this.userData;
          this.sessionService.storeSessionData(
            this.sessionService.sessionKey_UserSession,
            userData
          );

          this.sessionService.pushFeature(
            userData.featureFlags
          );
        });

        this.featureFlagService
        .getFeatureFlagApi(FeatureFlag.CDB11729Arion)
        .then((cdb11729Arion) => {
          this.upsertFeatureFlag(FeatureFlag.CDB11729Arion, cdb11729Arion?.data ?? false);
        })
        .catch(() => {
          this.upsertFeatureFlag(FeatureFlag.CDB11729Arion, false);
        });
    }).catch(() => {
      this.router.navigate(['/unauthorized/401']);
    });
  }

  get userSession(): UserSessionData {
    return this.sessionService.getSessionData(this.sessionService.sessionKey_UserSession);
  }

  private upsertFeatureFlag(name: string, enabled: boolean): void {
    const existingFlag = this.userData.featureFlags.find((flag) => flag.name === name);

    if (existingFlag) {
      existingFlag.enabled = enabled;
    } else {
      const flag = new FeatureFlagModel();
      flag.name = name;
      flag.enabled = enabled;
      this.userData.featureFlags.push(flag);
    }

    const userData = this.userData;
    this.sessionService.storeSessionData(
      this.sessionService.sessionKey_UserSession,
      userData
    );

    this.sessionService.pushFeature(
      userData.featureFlags
    );
  }
}
